import { type ComponentPropsWithoutRef, memo, type ReactNode } from 'react';

import { ChevronRightIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import {
  WORKSPACE_AGENT_MY_TASKS_COMPACT_ROW_HEIGHT,
  WORKSPACE_AGENT_MY_TASKS_ROW_HEIGHT,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentMyTasksRowLayout';

export type WorkspaceAgentPanelMyTasksRowDensity = 'default' | 'compact';

export type WorkspaceAgentPanelMyTasksRowFrameProps = Omit<
  ComponentPropsWithoutRef<'button'>,
  'children' | 'className' | 'onClick' | 'type'
> & {
  density?: WorkspaceAgentPanelMyTasksRowDensity;
  icon: ReactNode;
  isSelected?: boolean;
  meta: ReactNode;
  onClick: () => void;
  overflowMenu: ReactNode;
  preview: ReactNode;
  showDrillInChevron?: boolean;
  status?: ReactNode;
  title: string;
};

export const WorkspaceAgentPanelMyTasksRowFrame = memo(
  ({
    density = 'default',
    icon,
    isSelected = false,
    meta,
    onClick,
    overflowMenu,
    preview,
    showDrillInChevron = true,
    status,
    title,
    ...buttonProps
  }: WorkspaceAgentPanelMyTasksRowFrameProps) => {
    const isCompact = density === 'compact';

    return (
      <div
        className="group relative w-full overflow-visible"
        style={{
          height: isCompact ? WORKSPACE_AGENT_MY_TASKS_COMPACT_ROW_HEIGHT : WORKSPACE_AGENT_MY_TASKS_ROW_HEIGHT,
        }}
      >
        <button
          {...buttonProps}
          className={cn(
            'group/row flex h-full w-full items-start gap-2 overflow-visible rounded-6 text-left transition-colors ads:hover:bg-hover-subtle hover:bg-gray-2 focus-visible:outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-altitude-8 aria-disabled:cursor-default ads:aria-disabled:hover:bg-transparent aria-disabled:hover:bg-transparent',
            isCompact ? 'px-2 py-1' : 'p-2',
            isSelected && 'ads:bg-hover-subtle bg-gray-2',
          )}
          onClick={onClick}
          type="button"
        >
          <div className="min-w-0 flex-1 overflow-visible">
            <div className="flex min-h-5 items-center gap-2 overflow-visible">
              {icon}
              <span className="flex min-w-0 flex-1 items-center">
                <span className="min-w-0 truncate font-medium ads:text-primary text-12 text-gray-12 leading-4">
                  {title}
                </span>
                {showDrillInChevron ? (
                  <span
                    aria-hidden
                    className="inline-flex w-0 shrink-0 items-center overflow-hidden opacity-0 transition-[width,margin,opacity] duration-150 ease-out group-focus-within/row:ml-1 group-focus-within/row:w-3 group-focus-within/row:opacity-100 group-hover/row:ml-1 group-hover/row:w-3 group-hover/row:opacity-100"
                  >
                    <ChevronRightIcon aria-hidden className="shrink-0 ads:text-icon-subtle text-gray-9" size="xs" />
                  </span>
                ) : null}
              </span>
            </div>
            {preview}
            {meta}
          </div>
          {status}
        </button>
        {overflowMenu}
      </div>
    );
  },
);

WorkspaceAgentPanelMyTasksRowFrame.displayName = 'WorkspaceAgentPanelMyTasksRowFrame';
